import { offlineServiceState } from '@/services/offline-map-service'
import type {
  GenerateOfflineMapFeaturesReturn,
  UnitType,
} from '@/services/offline-map-service/types'
import {
  getDistancesFromPoints,
  updateOfflineStateMapArea,
} from '@/services/offline-map-service/helpers'
import throttle from 'lodash/throttle'
import { baseBlueColor, baseGreenColor } from '@/lib/color'
import { camera, visibleRegion } from '@/services/map-service'
import { getItem } from '@/plugins/storage'
import { toMercator, toWgs84 } from '@turf/projection'
import bboxPolygon from '@turf/bbox-polygon'
import { polygon, points } from '@turf/turf'
import center from '@turf/center'
import { watch } from 'vue'

/**
 * Generates a rectangle GeoJson object to display draft map boundary
 * and generates two Point GeoJson objects to display the map width and height labels
 *
 * Called when we run `toggleOfflineMapOverlay` which is run when:
 * - On initial offline map creation when we display the initial offline map boundary
 * - A user updates draftMap.mapDetail when creating an offline map
 * - A user resizes a draft offline map and we call getDownloadSizeEstimate
 */
const generateOfflineMapFeatures = ():
  | GenerateOfflineMapFeaturesReturn
  | undefined => {
  if (!offlineServiceState.draftMap.maxArea) {
    return undefined
  }

  const extent = visibleRegion()
  const extentCoords = extent.geometry.coordinates[0]
  const { latitude: lat, longitude: lng } = camera()?.location ?? {}
  const ratio = 150 / 192
  const mapMargin = 0.75
  const mercatorCenter = toMercator([lng, lat])
  const maxArea = offlineServiceState.draftMap.maxArea * 1e6
  const unitType: UnitType = getItem('unitType')

  // IF area = height * width, AND width = height * ratio THEN height = sqrt(area / ratio)
  let height = Math.sqrt(maxArea / ratio)
  let width = maxArea / height

  // Use the map bounds to get the max width and height in projected units
  const mapExtentMercator = [
    toMercator(extentCoords[3]),
    toMercator(extentCoords[1]),
  ]
  const maxWidth =
    Math.abs(mapExtentMercator[1][0] - mapExtentMercator[0][0]) * mapMargin
  const maxHeight =
    Math.abs(mapExtentMercator[1][1] - mapExtentMercator[0][1]) * mapMargin

  // Scale the box if constrained in either direction
  const scale = Math.min(1, maxHeight / height, maxWidth / width)
  width = width * scale
  height = height * scale

  // Generate the rectangle geojson
  const innerRing = toWgs84(
    bboxPolygon([
      (mercatorCenter[0] ?? 0) - width / 2,
      (mercatorCenter[1] ?? 0) - height / 2,
      (mercatorCenter[0] ?? 0) + width / 2,
      (mercatorCenter[1] ?? 0) + height / 2,
    ])
  ).geometry.coordinates[0]

  const innerRingLng = innerRing[0][0]
  const outerRing = bboxPolygon([
    innerRingLng - 180,
    -90,
    innerRingLng + 180,
    90,
  ]).geometry.coordinates[0]
  const rectangle = polygon([outerRing, innerRing], {
    color: baseBlueColor.toRgbaString(),
  })
  rectangle.properties.color = baseGreenColor.toRgbaString()

  offlineServiceState.draftMap.shape = innerRing

  // Generate the label geoJson
  const widthCoords = points([innerRing[2], innerRing[3]])
  const heightCoords = points([innerRing[3], innerRing[4]])
  const widthLabel = center(widthCoords)
  const heightLabel = center(heightCoords)
  const unitLabel = unitType === 'imperial' ? 'mi' : 'km'

  const { widthDistance, heightDistance } = getDistancesFromPoints(
    innerRing[2],
    innerRing[3],
    innerRing[4],
    unitType
  )

  widthLabel.properties = {
    title: `${widthDistance.toFixed(1)} ${unitLabel}`,
    meta: 'widthLabel',
  }
  heightLabel.properties = {
    title: `${heightDistance.toFixed(1)} ${unitLabel}`,
    meta: 'heightLabel',
  }

  // Save offlineServiceState.map_area used in x-marketing-event offline_map_definition_saved
  updateOfflineStateMapArea(widthDistance, heightDistance, unitType)

  return [rectangle, widthLabel, heightLabel]
}

/**
 * Optimizes the animation of draft map shape that is visible on screen when the user pans around the map while creating an offline map
 */
const updateOfflineMapOverlay = () => {
  requestAnimationFrame(() => {
    const features = generateOfflineMapFeatures() ?? []
    offlineServiceState.overlayFeature = features
  })
}

/**
 * Toggles visible draft map displayed to the user when creating an offline map
 * Animation is throttled with offlineServiceState.offlineEventHandler in combination with generated features in the requestAnimationFrame callback
 */
export const toggleOfflineMapOverlay = () => {
  if (offlineServiceState.inCreateMode) {
    // Populate the layer to begin with
    updateOfflineMapOverlay()

    // Add watcher to update the layer on map move
    // Throttle makes repaint on map move slightly faster
    offlineServiceState.offlineEventHandler = throttle(
      () => updateOfflineMapOverlay(),
      20
    )

    offlineServiceState.unwatchOverlay = watch(
      camera,
      offlineServiceState.offlineEventHandler
    )
  } else {
    // Turn off the map watcher
    offlineServiceState.unwatchOverlay()
    // Remove all features from the layer
    requestAnimationFrame(() => {
      offlineServiceState.overlayFeature = []
    })
  }
}
